import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Phone } from 'lucide-react';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) return;
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPassword) return;
    setLoading(true);
    try {
      await register(regEmail, regPassword, regName);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left: Form */}
      <div className="flex flex-col items-center justify-center px-8 py-12 bg-white">
        <div className="w-full max-w-sm space-y-8">
          {/* Logo */}
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-black rounded-sm flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                CallRelay
              </span>
            </div>
            <p className="text-sm text-slate-500">
              PC-zu-Handy Anruflösung für Berater
            </p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="w-full rounded-sm bg-slate-100" data-testid="auth-tabs">
              <TabsTrigger value="login" className="flex-1 rounded-sm" data-testid="login-tab">
                Anmelden
              </TabsTrigger>
              <TabsTrigger value="register" className="flex-1 rounded-sm" data-testid="register-tab">
                Registrieren
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="login-email">E-Mail</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="berater@firma.de"
                    value={loginEmail}
                    onChange={e => setLoginEmail(e.target.value)}
                    className="rounded-sm"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Passwort</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="Passwort"
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    className="rounded-sm"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-sm bg-black text-white hover:bg-slate-800"
                  data-testid="login-submit-button"
                >
                  {loading ? 'Anmelden...' : 'Anmelden'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="reg-name">Name</Label>
                  <Input
                    id="reg-name"
                    type="text"
                    placeholder="Max Mustermann"
                    value={regName}
                    onChange={e => setRegName(e.target.value)}
                    className="rounded-sm"
                    data-testid="register-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email">E-Mail</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="berater@firma.de"
                    value={regEmail}
                    onChange={e => setRegEmail(e.target.value)}
                    className="rounded-sm"
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Passwort</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    placeholder="Min. 6 Zeichen"
                    value={regPassword}
                    onChange={e => setRegPassword(e.target.value)}
                    className="rounded-sm"
                    data-testid="register-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-sm bg-black text-white hover:bg-slate-800"
                  data-testid="register-submit-button"
                >
                  {loading ? 'Registrieren...' : 'Konto erstellen'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Right: Hero image */}
      <div className="hidden lg:block relative bg-slate-900">
        <img
          src="https://images.pexels.com/photos/5712072/pexels-photo-5712072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Berater am Telefon"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <h2 className="text-3xl font-bold text-white tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ein Klick am PC.
            <br />
            Dein Handy klingelt.
          </h2>
          <p className="text-slate-300 text-base max-w-md">
            Modulare Anruflösung für den Recruiting-Alltag. Schnell, sicher, unkompliziert.
          </p>
        </div>
      </div>
    </div>
  );
}
